import { Component, OnInit, inject, input } from '@angular/core';
import { TranslateDirective } from '@ngx-translate/core';
import { FlyoutMenuComponent } from '../../components/flyout-menu/flyout-menu.component';
import { FlyoutMenuItemDirective } from '../../components/flyout-menu/flyout-menu-item.directive';
import { FlyoutMenuToggleDirective } from '../../components/flyout-menu/flyout-menu-toggle.directive';
import { HrefDirective } from '../../directives/href/href.directive';
import { IconComponent } from '../../components/icon/icon.component';
import { SecurityService } from '../../services/security.service';
import { Account } from '../../domain/account.domain';

/**
 * The Documents Link component
 *
 * This component presents links to eservices documents on the accounts pages
 */
@Component({
  selector: 'td-wb-documents-link',
  templateUrl: 'documents-link.component.html',
  styleUrls: ['documents-link.component.scss'],
  imports: [
    TranslateDirective,
    HrefDirective,
    IconComponent,
    FlyoutMenuToggleDirective,
    FlyoutMenuComponent,
    FlyoutMenuItemDirective
  ]
})
export class DocumentsLinkComponent implements OnInit {
  private readonly securityService = inject(SecurityService);

  readonly account = input<Account>(undefined);

  statementsPermission: boolean;
  taxStatementsPermission: boolean;
  portfolioReportsPermission: boolean;
  confirmationsPermission: boolean;

  /**
   * Check user permissions on initialization
   */
  ngOnInit(): void {
    this.statementsPermission = this.securityService.hasPermission('ESERVICES_STATEMENTS');
    this.taxStatementsPermission = this.securityService.hasPermission('ESERVICES_TAX_DOCUMENTS');
    this.portfolioReportsPermission = this.securityService.hasPermission('ESERVICES_PORTFOLIO_REPORTS');
    this.confirmationsPermission = this.securityService.hasPermission('ESERVICES_CONFIRMATIONS');
  }

  showStatements(): boolean {
    return this.statementsPermission;
  }

  showTaxStatements(): boolean {
    return this.taxStatementsPermission && this.hasTaxDocsAccountLevelPermission();
  }

  showPortfolioReports(): boolean {
    return this.portfolioReportsPermission;
  }

  showConfirmations(): boolean {
    return this.confirmationsPermission;
  }

  showEservicesLinks(): boolean {
    return this.hasEservicesAccountLevelPermission() &&
      (this.showStatements() || this.showTaxStatements() || this.showPortfolioReports() || this.showConfirmations());
  }

  getAccountGroupId(): string {
    return this.account().groupId;
  }

  private hasEservicesAccountLevelPermission(): boolean {
    return this.securityService.hasAccountPermission(this.account(), 'ACCOUNT_ESERVICES');
  }

  private hasTaxDocsAccountLevelPermission(): boolean {
    return this.securityService.hasAccountPermission(this.account(), 'ACCOUNT_ESERVICES_TAX_DOCUMENTS');
  }
}
